/**
 * Shared filter translation layer for Dashboard ↔ Reports ↔ Analysis synchronization.
 *
 * KPI Definition Matrix — canonical metric, backend date field, and drill-down destination:
 * ┌──────────────────────────┬────────────────────────────┬──────────────────────────────────────────────────────┐
 * │ Dashboard KPI            │ Backend date field         │ Drill-down destination                               │
 * ├──────────────────────────┼────────────────────────────┼──────────────────────────────────────────────────────┤
 * │ Total / New Prospect     │ created_at                 │ /reports/company           (periodKey: 'period')     │
 * │ Converted to Proposal    │ proposal_date              │ /reports/proposals         (periodKey: 'proposalDate')│
 * │ Total Leads              │ created_at                 │ /reports/leads             (periodKey: 'period')     │
 * │ Total Projects           │ project_date               │ /reports/projects          (periodKey: 'projectStartDate')│
 * │ Leads Updated            │ updated_at (lead)          │ /reports/leads             (periodKey: 'leadUpdatePeriod')│
 * │ Proposals Updated        │ updated_at (proposal)      │ /reports/proposals         (periodKey: 'updatedAt')  │
 * │ Not Converted Proposals  │ created_at                 │ /reports/proposals         (periodKey: 'createdAt', projectStatus=Not Converted)│
 * │ Converted to Project     │ created_at (project)       │ /reports/projects          (periodKey: 'createdAt') │
 * │ Task Updated Leads       │ task updated_at            │ /reports/leads             (periodKey: 'taskUpdatePeriod')│
 * │ Sales Funnel – Leads     │ created_at                 │ /reports/leads             (periodKey: 'period')     │
 * │ Sales Funnel – Proposal  │ proposal_date              │ /reports/proposals         (periodKey: 'proposalDate')│
 * │ Sales Funnel – Stage     │ stage_updated_at           │ /reports/proposals         (periodKey: 'stageUpdatePeriod')│
 * └──────────────────────────┴────────────────────────────┴──────────────────────────────────────────────────────┘
 *
 * Canonical field mapping (Dashboard → Analysis):
 *   people        → salesCreator
 *   businessUnits → department
 *   source        → leadSource
 *   sector        → sector   (unchanged)
 *   standard      → standard (unchanged; sent as `service` to backend via buildAnalysisPayload)
 *   service       → service  (unchanged; product/service-category filter)
 *   branchId      → branchId
 *   taggingIds    → taggingIds
 *   period        → period   (unchanged)
 *   customStartDate / customEndDate → startDate / endDate
 */

/**
 * Maps each report period-key to the correct custom-date-range param name pair.
 * When period === 'custom', use this to find the startDate/endDate param names
 * accepted by the destination report page.
 */
export const PERIOD_KEY_CONFIGS = {
  period:            { startParam: 'customStartDate',       endParam: 'customEndDate'        },
  proposalDate:      { startParam: 'proposalDateStartDate', endParam: 'proposalDateEndDate'  },
  createdAt:         { startParam: 'createdAtStartDate',    endParam: 'createdAtEndDate'     },
  stageUpdatePeriod: { startParam: 'stageUpdateStartDate',  endParam: 'stageUpdateEndDate'   },
  updatedAt:         { startParam: 'updatedAtStartDate',    endParam: 'updatedAtEndDate'     },
  leadUpdatePeriod:  { startParam: 'leadUpdateStartDate',   endParam: 'leadUpdateEndDate'    },
  taskUpdatePeriod:  { startParam: 'taskUpdateStartDate',   endParam: 'taskUpdateEndDate'    },
  projectStartDate:  { startParam: 'projectStartDateFrom',  endParam: 'projectStartDateTo'   },
  projectEndDate:    { startParam: 'projectEndDateFrom',    endParam: 'projectEndDateTo'     },
  auditDate:         { startParam: 'proposeDateFrom',       endParam: 'proposeDateTo'        },
};

/**
 * Build URLSearchParams for navigating from Dashboard to a Report page.
 * Encapsulates the canonical filter → report URL param mapping so each
 * drill-down link uses exactly the same logic.
 *
 * @param {Object} serializedFilters - Serialized filter values from dashboard state.
 *   Each value is either `'all'` or a comma-separated ID string (e.g. `'1,2,3'`).
 *   Expected keys: people, businessUnits, sector, standard, source.
 * @param {string} rawPeriod - Current period value from dashboard filters
 *   (e.g. `'this_month'`, `'custom'`, `'all'`).
 * @param {Object} customDateRange - `{ startDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' }`.
 * @param {Object} [config={}] - Destination-specific configuration:
 *   - periodKey        {string}  Query param name for the period on the destination page.
 *                                Default: `'period'`.
 *   - defaultPeriod    {string|null} Period to use when rawPeriod is `'all'` or absent.
 *                                Pass `null` (default) to omit the period param in that case.
 *   - peopleKey        {string}  Override param name for the people filter.
 *                                Default: `'people'`. Pass `null` to skip.
 *   - businessUnitsKey {string}  Override param name for the businessUnits filter.
 *                                Default: `'businessUnits'`. Pass `null` to skip.
 *   - extraParams      {Object}  Additional static key→value params to append.
 * @returns {URLSearchParams}
 */
export function buildReportNavParams(serializedFilters, rawPeriod, customDateRange, config = {}) {
  const {
    periodKey = 'period',
    defaultPeriod = null,
    peopleKey = 'people',
    businessUnitsKey = 'businessUnits',
    extraParams = {},
  } = config;

  const params = new URLSearchParams();

  // Canonical dimension filters
  if (peopleKey && serializedFilters.people !== 'all') {
    params.append(peopleKey, serializedFilters.people);
  }
  if (businessUnitsKey && serializedFilters.businessUnits !== 'all') {
    params.append(businessUnitsKey, serializedFilters.businessUnits);
  }
  if (serializedFilters.sector !== 'all') {
    params.append('sector', serializedFilters.sector);
  }
  if (serializedFilters.standard !== 'all') {
    params.append('standard', serializedFilters.standard);
  }
  if (serializedFilters.service !== 'all') {
    params.append('service', serializedFilters.service);
  }
  if (serializedFilters.source !== 'all') {
    params.append('source', serializedFilters.source);
  }

  // Period / date range
  const effectivePeriod = (rawPeriod && rawPeriod !== 'all') ? rawPeriod : defaultPeriod;
  if (effectivePeriod) {
    params.append(periodKey, effectivePeriod);
    if (effectivePeriod === 'custom') {
      const datePair = PERIOD_KEY_CONFIGS[periodKey];
      if (datePair && customDateRange?.startDate && customDateRange?.endDate) {
        params.append(datePair.startParam, customDateRange.startDate);
        params.append(datePair.endParam, customDateRange.endDate);
      }
    }
  }

  // Extra static params (e.g. proposalStage, projectStatus, statusApproval)
  Object.entries(extraParams).forEach(([k, v]) => {
    if (v != null && v !== '') params.append(k, String(v));
  });

  return params;
}

/**
 * Map canonical dashboard filter state to Analysis page filter state.
 * Used when generating a link from Dashboard → Analysis so both pages
 * represent the same data window.
 *
 * @param {Object} dashboardFilters - Raw dashboard filter object.
 *   Expected keys: people (array), businessUnits (array), source (array),
 *   sector (array), standard (array), branchId (string), taggingIds (array), period (string).
 * @param {Object} customDateRange - `{ startDate, endDate }`.
 * @returns {{ filters: Object, customDateRange: Object }}
 */
export function mapDashboardFiltersToAnalysis(dashboardFilters, customDateRange) {
  const toIdArray = (v) => {
    if (!v) return [];
    if (Array.isArray(v)) return v.map(String).filter(Boolean);
    if (v === 'all') return [];
    return String(v).split(',').map((s) => s.trim()).filter(Boolean);
  };

  return {
    filters: {
      period: dashboardFilters.period || 'this_year',
      salesCreator: toIdArray(dashboardFilters.people),
      department: toIdArray(dashboardFilters.businessUnits),
      sector: toIdArray(dashboardFilters.sector),
      leadSource: toIdArray(dashboardFilters.source),
      standard: toIdArray(dashboardFilters.standard),
      branchId: dashboardFilters.branchId == null || dashboardFilters.branchId === '' || dashboardFilters.branchId === 'all'
        ? ''
        : String(dashboardFilters.branchId),
      taggingIds: toIdArray(dashboardFilters.taggingIds),
      proposalStage: [],
      proposalApprovalStatus: '',
      competitor: [],
      lostReason: [],
    },
    customDateRange: {
      startDate: customDateRange?.startDate || '',
      endDate: customDateRange?.endDate || '',
    },
  };
}

/**
 * Parse Analysis page URL search params into Analysis filter state.
 * Accepts both canonical Dashboard param names and Analysis-native names
 * so inbound links from Dashboard (and direct bookmarks) both work.
 *
 * Param name aliases supported per field:
 *   salesCreator   ← salesCreator | people
 *   department     ← department   | businessUnits
 *   leadSource     ← leadSource   | source
 *   sector         ← sector
 *   standard       ← standard
 *   proposalStage  ← proposalStage
 *   branchId       ← branchId
 *   taggingIds     ← taggingIds
 *   period         ← period                  (default: 'this_year')
 *   customDateRange ← startDate | customStartDate  /  endDate | customEndDate
 *
 * @param {URLSearchParams} searchParams
 * @returns {{ filters: Object, customDateRange: Object }}
 */
export function parseAnalysisUrlParams(searchParams) {
  const splitParam = (...keys) => {
    for (const key of keys) {
      const v = searchParams.get(key);
      if (v && v !== 'all') return v.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [];
  };

  const period = searchParams.get('period') || 'this_year';
  const startDate =
    searchParams.get('startDate') || searchParams.get('customStartDate') || '';
  const endDate =
    searchParams.get('endDate') || searchParams.get('customEndDate') || '';

  return {
    filters: {
      period,
      salesCreator: splitParam('salesCreator', 'people'),
      department: splitParam('department', 'businessUnits'),
      sector: splitParam('sector'),
      leadSource: splitParam('leadSource', 'source'),
      standard: splitParam('standard'),
      branchId: (() => {
        const value = searchParams.get('branchId') || '';
        return value === 'all' ? '' : value;
      })(),
      taggingIds: splitParam('taggingIds'),
      proposalStage: splitParam('proposalStage'),
      proposalApprovalStatus: searchParams.get('proposalApprovalStatus') || '',
      competitor: splitParam('competitor'),
      lostReason: splitParam('lostReason'),
    },
    customDateRange: { startDate, endDate },
  };
}
